import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { User } from "firebase/auth";

interface PathParameters {
	[key: string]: any;
}

const DISABLE_TIMEOUT = 0;
// eslint-disable-next-line
const ONE_MINUTE_DURATION_IN_MS = 60 * 1000;
// eslint-disable-next-line
const TEN_SECOND_DURATION_IN_MS = 10 * 1000;

export default class ApiInterface {
	private callerInstance: AxiosInstance;
	private user?: User;

	constructor(endpoint: string, user?: User) {
		this.callerInstance = axios.create({
			baseURL: endpoint,
			timeout: DISABLE_TIMEOUT
		});
		this.user = user;
	}

	private substitutePathParams(path: string, pathParams: PathParameters) {
		const formattedPath = Object.keys(pathParams).reduce(
			(formattedPath, paramKey) =>
				formattedPath.replace(`{${paramKey}}`, pathParams[paramKey].toString()),
			path
		);
		const unsubstitutedParams = [...formattedPath.matchAll(/(?<={)\w+(?=})/g)];
		if (unsubstitutedParams.length) {
			throw new Error(
				`The following path parameters were not provided: ${unsubstitutedParams.join(", ")}`
			);
		}
		return formattedPath;
	}

	private async authorizeAxiosConfig(config?: AxiosRequestConfig) {
		if (this.user) {
			const idToken = await this.user.getIdToken();
			return {
				...config,
				headers: {
					Authorization: `Bearer ${idToken}`,
					// Provided config may override automatic authorization
					...config?.headers
				}
			};
		} else {
			return config;
		}
	}

	private getResponseData(res: AxiosResponse): {} | any {
		return res.status === 204 ? {} : res.data;
	}

	public async get(
		httpPathExtension: string,
		pathParams: PathParameters = {},
		config?: AxiosRequestConfig
	) {
		// Substitute URL path parameters
		const formattedPath = this.substitutePathParams(
			httpPathExtension,
			pathParams
		);
		// Authorize request with token
		config = await this.authorizeAxiosConfig(config);
		// Optional URL query parameters must be passed via the Axios request config as "params"
		return this.callerInstance
			.get(formattedPath, config)
			.then(this.getResponseData);
	}

	public async post(
		httpPathExtension: string,
		data?: any,
		pathParams: PathParameters = {},
		config?: AxiosRequestConfig
	) {
		const formattedPath = this.substitutePathParams(
			httpPathExtension,
			pathParams
		);
		// Authorize request with token
		config = await this.authorizeAxiosConfig(config);
		return this.callerInstance
			.post(formattedPath, data, config)
			.then(this.getResponseData);
	}

	public async put(
		httpPathExtension: string,
		data?: any,
		pathParams: PathParameters = {},
		config?: AxiosRequestConfig
	) {
		const formattedPath = this.substitutePathParams(
			httpPathExtension,
			pathParams
		);
		// Authorize request with token
		config = await this.authorizeAxiosConfig(config);
		return this.callerInstance
			.put(formattedPath, data, config)
			.then(this.getResponseData);
	}

	public async patch(
		httpPathExtension: string,
		data?: any,
		pathParams: PathParameters = {},
		config?: AxiosRequestConfig
	): Promise<any> {
		// Substitute URL path parameters
		const formattedPath = this.substitutePathParams(
			httpPathExtension,
			pathParams
		);
		// Authorize request with token
		config = await this.authorizeAxiosConfig(config);
		// Optional URL query parameters must be passed via the Axios request config as "params"
		return this.callerInstance
			.patch(formattedPath, data, config)
			.then(this.getResponseData);
	}

	public async delete(
		httpPathExtension: string,
		data?: any,
		pathParams: PathParameters = {},
		config?: AxiosRequestConfig
	): Promise<any> {
		const formattedPath = this.substitutePathParams(
			httpPathExtension,
			pathParams
		);
		config = await this.authorizeAxiosConfig(config);
		return this.callerInstance
			.delete(formattedPath, { ...config, data })
			.then(this.getResponseData);
	}
}
